import { getCookieInClientSide } from "@/lib/utils/get-cookies-for-client";
import { useSyncExternalStore } from "react";

const DEFAULT_LOCALE = "kr";

function getLocaleSnapshot() {
  return getCookieInClientSide("NEXT_LOCALE") ?? DEFAULT_LOCALE;
}

function getServerLocaleSnapshot() {
  // Return default for SSR
  return DEFAULT_LOCALE;
}

// Subscribers to locale changes
const localeSubscribers = new Set<() => void>();

function subscribeToLocale(callback: () => void) {
  localeSubscribers.add(callback);

  // Poll for cookie changes every 300ms
  const intervalId = setInterval(() => {
    callback();
  }, 300);

  // Also listen to storage events (for cross-tab changes)
  const handleStorageChange = () => {
    callback();
  };
  window.addEventListener("storage", handleStorageChange);
  window.addEventListener("focus", callback);

  return () => {
    localeSubscribers.delete(callback);
    clearInterval(intervalId);
    window.removeEventListener("storage", handleStorageChange);
    window.removeEventListener("focus", callback);
  };
}

/**
 * Hook to get the current locale from the NEXT_LOCALE cookie.
 * Automatically updates when the cookie changes.
 * Uses useSyncExternalStore for efficient reactivity.
 */
export function useLocale() {
  return useSyncExternalStore(
    subscribeToLocale,
    getLocaleSnapshot,
    getServerLocaleSnapshot
  );
}
